import React from "react";
import { useSelector } from "react-redux";
import LandingHeader from "../components/landing/LandingHeader";
import LandingFooter from "../components/landing/LandingFooter";

const PrivacyPolicy = () => {
  const translation = useSelector((state) => state.translation);

  return (
    <div className="privacy-page">
      <LandingHeader />
      <main className="privacy-main">
        <div className="policy-hero-wrap">
          <div className="privacy-container">
            <div className="privacy-header">
              <h1>{translation.privacy_title || "Privacy Policy"}</h1>
              <p className="privacy-date">{translation.privacy_effective_date || "Effective Date: January 21, 2026"}</p>
            </div>
          </div>
        </div>
        <div className="privacy-container content-area-container">

          <p className="privacy-intro">
            {translation.privacy_intro ||
              "esyncing respects your privacy. This Privacy Policy explains how we collect, use, protect, and share your information when you use our platform. We are committed to transparency and giving you control over your data."}
          </p>

          <section className="privacy-section">
            <h2>{translation.privacy_section1_title || "1. Information We Collect"}</h2>
            <div className="privacy-subsection">
              <h3>{translation.privacy_section1a_title || "a) Information You Provide"}</h3>
              <p>{translation.privacy_section1a_text || "When you use esyncing, you may provide us with information:"}</p>
              <ul>
                <li>{translation.privacy_section1a_item1 || "Name"}</li>
                <li>{translation.privacy_section1a_item2 || "Email address"}</li>
                <li>{translation.privacy_section1a_item3 || "Company name"}</li>
                <li>{translation.privacy_section1a_item4 || "Account details"}</li>
                <li>{translation.privacy_section1a_item5 || "Support communications"}</li>
              </ul>
            </div>
            <div className="privacy-subsection">
              <h3>{translation.privacy_section1b_title || "b) Automatically Collected Information"}</h3>
              <p>{translation.privacy_section1b_text || "When you interact with the platform, we automatically collect:"}</p>
              <ul>
                <li>{translation.privacy_section1b_item1 || "IP address"}</li>
                <li>{translation.privacy_section1b_item2 || "Browser and device information"}</li>
                <li>{translation.privacy_section1b_item3 || "Usage data"}</li>
                <li>{translation.privacy_section1b_item4 || "Log and diagnostic data"}</li>
              </ul>
            </div>
            <div className="privacy-subsection">
              <h3>{translation.privacy_section1c_title || "c) Content and Workspace Data"}</h3>
              <p>{translation.privacy_section1c_text || "Your workspace may contain:"}</p>
              <ul>
                <li>{translation.privacy_section1c_item1 || "Chat messages"}</li>
                <li>{translation.privacy_section1c_item2 || "Uploaded files and documents"}</li>
                <li>{translation.privacy_section1c_item3 || "Knowledge base content"}</li>
                <li>{translation.privacy_section1c_item4 || "AI training questions"}</li>
                <li>{translation.privacy_section1c_item5 || "Demo workflows and interaction configurations"}</li>
              </ul>
              <p>
                {translation.privacy_section1c_footer ||
                  "Your content remains your data. We process it only to provide the service you requested."}
              </p>
            </div>
          </section>

          <section className="privacy-section">
            <h2>{translation.privacy_section2_title || "2. How We Use Your Information"}</h2>
            <p>{translation.privacy_section2_text || "We use your information to:"}</p>
            <ul>
              <li>{translation.privacy_section2_item1 || "Operate and maintain the platform"}</li>
              <li>{translation.privacy_section2_item2 || "Provide AI-powered features"}</li>
              <li>{translation.privacy_section2_item3 || "Improve product performance"}</li>
              <li>{translation.privacy_section2_item4 || "Provide customer support"}</li>
              <li>{translation.privacy_section2_item5 || "Ensure security and prevent misuse"}</li>
              <li>{translation.privacy_section2_item6 || "Communicate updates or platform improvements"}</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>{translation.privacy_section3_title || "3. AI & Data Usage"}</h2>
            <p>
              {translation.privacy_section3_text ||
                "esyncing uses AI to provide intelligent responses and automation. Here's how your data is handled:"}
            </p>
            <ul>
              <li>{translation.privacy_section3_item1 || "AI responses are generated using your workspace data"}</li>
              <li>{translation.privacy_section3_item2 || "We do not train our models on your workspace data unless you explicitly opt in"}</li>
              <li>{translation.privacy_section3_item3 || "Your data is kept under your control at all times"}</li>
              <li>{translation.privacy_section3_item4 || "All AI tools are context-based and respect your consent"}</li>
            </ul>
            <p>
              {translation.privacy_section3_footer ||
                "We are committed to protecting your data privacy while delivering powerful AI features."}
            </p>
          </section>

          <section className="privacy-section">
            <h2>{translation.privacy_section4_title || "4. Data Sharing & Third Parties"}</h2>
            <p>
              {translation.privacy_section4_text ||
                "We do not sell your data. We may share limited information with trusted third-party providers for:"}
            </p>
            <ul>
              <li>{translation.privacy_section4_item1 || "Hosting and infrastructure"}</li>
              <li>{translation.privacy_section4_item2 || "Analytics and performance monitoring"}</li>
              <li>{translation.privacy_section4_item3 || "Customer support tools"}</li>
              <li>{translation.privacy_section4_item4 || "Payment processing"}</li>
            </ul>
            <p>
              {translation.privacy_section4_footer ||
                "These providers only access data necessary to perform their functions and are required to protect your data."}
            </p>
          </section>

          <section className="privacy-section">
            <h2>{translation.privacy_section5_title || "5. Integrations & External Services"}</h2>
            <p>
              {translation.privacy_section5_text ||
                "esyncing allows you to connect third-party services such as Google Workspace, Slack, and WhatsApp."}
            </p>
            <ul>
              <li>{translation.privacy_section5_item1 || "Connections are only made with your explicit approval"}</li>
              <li>{translation.privacy_section5_item2 || "You control which integrations are active"}</li>
              <li>{translation.privacy_section5_item3 || "You can disconnect integrations at any time"}</li>
            </ul>
            <p>
              {translation.privacy_section5_footer ||
                "We recommend reviewing the privacy policies of any third-party services you connect to esyncing."}
            </p>
          </section>

          <section className="privacy-section">
            <h2>{translation.privacy_section6_title || "6. Data Security"}</h2>
            <p>
              {translation.privacy_section6_text ||
                "We take data security seriously and use industry-standard practices to protect your information."}
            </p>
            <ul>
              <li>{translation.privacy_section6_item1 || "Secure infrastructure with trusted cloud providers"}</li>
              <li>{translation.privacy_section6_item2 || "Encryption of data in transit and at rest where appropriate"}</li>
              <li>{translation.privacy_section6_item3 || "Access controls to limit who can view your data"}</li>
              <li>{translation.privacy_section6_item4 || "Ongoing monitoring for security threats"}</li>
            </ul>
            <p>
              {translation.privacy_section6_footer ||
                "While no system is completely secure, we continuously work to protect your data from unauthorized access or disclosure."}
            </p>
          </section>

          <section className="privacy-section">
            <h2>{translation.privacy_section7_title || "7. Data Retention"}</h2>
            <p>{translation.privacy_section7_text || "We retain your data as follows:"}</p>
            <ul>
              <li>{translation.privacy_section7_item1 || "Data is retained while your account is active"}</li>
              <li>{translation.privacy_section7_item2 || "After account deletion, data may be removed or anonymized"}</li>
              <li>{translation.privacy_section7_item3 || "Some data may be retained for legal or compliance reasons"}</li>
            </ul>
            <p>
              {translation.privacy_section7_footer ||
                "You can request data deletion by contacting our support team."}
            </p>
          </section>

          <section className="privacy-section">
            <h2>{translation.privacy_section8_title || "8. Your Rights"}</h2>
            <p>{translation.privacy_section8_text || "You have the following rights regarding your personal data:"}</p>
            <ul>
              <li>{translation.privacy_section8_item1 || "Access your personal data"}</li>
              <li>{translation.privacy_section8_item2 || "Update or correct your information"}</li>
              <li>{translation.privacy_section8_item3 || "Request deletion of your data"}</li>
              <li>{translation.privacy_section8_item4 || "Export your data"}</li>
              <li>{translation.privacy_section8_item5 || "Withdraw consent for data processing"}</li>
            </ul>
            <p>
              {translation.privacy_section8_footer ||
                "To exercise these rights, please contact us using the information provided at the end of this policy."}
            </p>
          </section>

          <section className="privacy-section">
            <h2>{translation.privacy_section9_title || "9. Cookies & Tracking"}</h2>
            <p>{translation.privacy_section9_text || "esyncing uses cookies and similar tracking technologies to:"}</p>
            <ul>
              <li>{translation.privacy_section9_item1 || "Enable core platform functionality"}</li>
              <li>{translation.privacy_section9_item2 || "Analyze usage to improve performance"}</li>
              <li>{translation.privacy_section9_item3 || "Remember your preferences"}</li>
            </ul>
            <p>
              {translation.privacy_section9_footer ||
                "You can manage cookies through your browser settings. Note that disabling cookies may impact platform functionality."}
            </p>
          </section>

          <section className="privacy-section">
            <h2>{translation.privacy_section10_title || "10. Children's Privacy"}</h2>
            <p>
              {translation.privacy_section10_text ||
                "esyncing is not intended for use by children under the age of 13. We do not knowingly collect personal information from children."}
            </p>
            <p>
              {translation.privacy_section10_footer ||
                "If we become aware we have collected personal information from a child under 13, we will delete that information immediately."}
            </p>
          </section>

          <section className="privacy-section">
            <h2>{translation.privacy_section11_title || "11. Changes to This Policy"}</h2>
            <p>
              {translation.privacy_section11_text ||
                "We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements."}
            </p>
            <ul>
              <li>{translation.privacy_section11_item1 || "The effective date at the top will be updated"}</li>
              <li>{translation.privacy_section11_item2 || "For significant changes, we may notify you by email or platform notification"}</li>
              <li>{translation.privacy_section11_item3 || "We encourage you to review this policy periodically"}</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>{translation.privacy_section12_title || "12. Contact Us"}</h2>
            <p>{translation.privacy_section12_text || "If you have questions or concerns about this privacy policy, please contact us:"}</p>
            <div className="privacy-contact">
              <div className="contact-card">
                <h4>{translation.privacy_section12_card_title || "Email"}</h4>
                <p>{translation.privacy_section12_card_email || "privacy@esyncing.com"}</p>
                <span>{translation.privacy_section12_card_note || "We can also be reached through the platform's contact page."}</span>
              </div>
            </div>
          </section>

          <p className="privacy-updated">
            {translation.privacy_last_updated || "This privacy policy was last updated on January 27, 2026."}
          </p>
        </div>
      </main>
      <LandingFooter />
    </div>
  );
};

export default PrivacyPolicy;
